import { debounce } from '@bimser/core';
import classNames from 'classnames/bind';
import _at from 'lodash/at';
import _groupBy from 'lodash/groupBy';
import React, { JSX } from 'react';
import { IInputChangeEventArgs } from '../../BCInput';
import BCScrollbars from '../../BCScrollbars';
import Styles from '../assets/style.scss';
import { ICollapseItem, IPropertyGroupProps, IPropertyItem } from '../entities';
import PropertyItem from './PropertyItem';
import PropertySearchBox from './PropertySearchBox';

const cx = classNames.bind(Styles);

const PropertyGroup = React.memo((props: IPropertyGroupProps) => {
	const [searchKey, setSearchKey] = React.useState('');
	const onSearchChange = debounce(
		(args: IInputChangeEventArgs) => setSearchKey(args.data.toLocaleLowerCase()),
		300
	);

	const getPropertyData = React.useCallback(
		(key: string) => {
			if (!key.includes('.')) {
				if (Array.isArray(props.data[key])) {
					return [...props.data[key]];
				} else if (typeof props.data[key] === 'object') {
					return props.data[key] ? { ...props.data[key] } : null;
				} else {
					return props.data[key];
				}
			}
			return _at(props.data, key)[0];
		},
		[props.data]
	);

	const renderProperties = (group: ICollapseItem, propertyItems: Array<IPropertyItem>) => {
		const mostUsedGroupKey = 'mostUsedPropertiesCollapse_' + props.tabKey;

		const _propertyItems =
			propertyItems?.filter(property => {
				if (searchKey && group.key === mostUsedGroupKey) {
					return false;
				}
				return property.label.toLowerCase().includes(searchKey);
			}) || [];

		if (!_propertyItems.length) {
			return false;
		}

		const groupedItems = _groupBy(_propertyItems, 'groupName');
		const content: JSX.Element[] = [];

		const groupedItemKeys = Object.keys(groupedItems);
		groupedItemKeys.forEach(groupedItem => {
			const propertyGroup =
				groupedItem !== 'undefined' && groupedItem !== 'null' ? true : false;
			const classNames = cx({
				propertyInspector: true,
				propertyGroup
			});

			const groupedContent = groupedItems[groupedItem].map((property: IPropertyItem) => (
				<PropertyItem
					currentLanguage={props.currentLanguage}
					supportedLanguages={props.supportedLanguages}
					propertyItem={property}
					key={property.key}
					onValueChanged={props.onValueChanged}
					reload={props.reload}
					fireAction={props.fireAction}
					value={props.data ? getPropertyData(property.key) : null}
					getOptionsPromise={props.getOptionsPromise}
					onRefresh={props.onRefresh}
					guid={props.data?.controlId ?? props.guid}
					commonPopoverItems={props.commonPopoverItems}
					showCustomBadgeOnLabel={props.showCustomBadgeOnLabel}
					readOnlyDataProperties={props.readOnlyDataProperties}
					translate={props.translate}
				/>
			));

			content.push(
				<div
					className={classNames}
					data-title={groupedItem}
					key={groupedItem}
				>
					{groupedContent}
				</div>
			);
		});

		const hasGroupTitle = groupedItemKeys?.filter(i => i !== 'null')?.length ? true : false;
		if (hasGroupTitle) {
			return groupWrapper(content);
		}

		return content;
	};

	const groupWrapper = (child: JSX.Element[]) => (
		<div className={Styles.propertyGroupWrapper}>{child}</div>
	);

	const renderSearch = () => {
		if (props.searchable !== false) {
			return <PropertySearchBox onChange={onSearchChange} />;
		}
	};

	const renderGroups = () => {
		const content = [...props.groupItems]
			.sort((a, b) => a.orderNo - b.orderNo)
			.map(group => {
				const properties = renderProperties(group, group.properties);
				if (properties === false) {
					return false;
				}
				return (
					<div
						key={group.key}
						className={Styles.group}
					>
						<div className={Styles.groupTitle}>
							<h3>{group.label}</h3>
							{group.addonAfter}
						</div>
						<div className={Styles.groupContent}>
							{renderProperties(group, group.properties)}
						</div>
					</div>
				);
			})
			.filter(Boolean);

		if (!content.length) {
			return null;
		}

		return content;
	};

	return (
		<>
			{renderSearch()}
			<BCScrollbars
				styles={props.scrollbarStyles}
				cssClass={Styles.groupScrollbar}
			>
				<div className={Styles.groups}>{renderGroups()}</div>
			</BCScrollbars>
		</>
	);
});

export default PropertyGroup;
